import {
    ChevronDown,
    ChevronRight,
    Clock,
    FileText,
    PiggyBank,
    Plus,
    TrendingUp,
    UserPlus,
    Users,
    Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getShop } from '../../api/shop.api';
import styles from '../../assets/scss/pages/AdminDashboard.module.scss';
import api from '../../config/axios-conf';
import { getSocket } from '../../services/socket.service';
import { formatCurrency } from '../../utils/formatters';

const AVATAR_COLORS = ['#0098F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

function getColor(name) {
    if (!name) return AVATAR_COLORS[0];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function getInitials(name) {
    if (!name) return '?';
    return name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

function formatTime(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diff < 1) return 'Vừa xong';
    if (diff < 60) return `${diff} phút`;
    return `${Math.floor(diff / 60)}h ${diff % 60}p`;
}

function formatDateVN(date) {
    const d = date || new Date();
    const day = d.getDate();
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    return `${day} Tháng ${month}, ${year}`;
}

const STATUS_LABELS = {
    paid: 'HOÀN TẤT',
    partial: 'ĐANG XỬ LÝ',
    pending: 'ĐANG XỬ LÝ',
    unpaid: 'CHỜ TT',
    cancelled: 'ĐÃ HUỶ',
};

const STATUS_CLASS = {
    paid: 'paid',
    partial: 'pending',
    pending: 'pending',
    unpaid: 'pending',
    cancelled: 'cancelled',
};

const STATUS_STYLE_MAP = {
    paid: styles.dashboardBillStatusPaid,
    pending: styles.dashboardBillStatusPending,
    cancelled: styles.dashboardBillStatusCancelled,
};

const CHART_PERIODS = [
    { value: '7days', label: '7 ngày qua' },
    { value: 'thismonth', label: 'Tháng này' },
    { value: 'lastmonth', label: 'Tháng trước' },
];

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [recentBills, setRecentBills] = useState([]);
    const [checkinList, setCheckinList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [chartPeriod, setChartPeriod] = useState('7days');
    const [chartLoading, setChartLoading] = useState(false);
    const [showMoney, setShowMoney] = useState(false);
    const [periodDropdownOpen, setPeriodDropdownOpen] = useState(false);
    const [shopTimeEnd, setShopTimeEnd] = useState(null);
    const [expiryDismissed, setExpiryDismissed] = useState(
        () => localStorage.getItem('expiry_banner_dismissed') === '1'
    );
    const periodRef = useRef(null);

    // Đóng dropdown khi click ra ngoài
    useEffect(() => {
        if (!periodDropdownOpen) return;
        const handleClickOutside = (e) => {
            if (periodRef.current && !periodRef.current.contains(e.target)) {
                setPeriodDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [periodDropdownOpen]);

    // Fetch chỉ stats (KPI) - dùng cho realtime update, không loading
    const fetchStats = useCallback(async () => {
        try {
            const { data } = await api.get('/statistics/today');
            setStats(data);
        } catch (err) {
            console.error('Stats fetch error:', err);
        }
    }, []);

    // Fetch chỉ bills gần đây - dùng cho realtime update
    const fetchBills = useCallback(async () => {
        try {
            const { data } = await api.get('/bills/billAll', { params: { limit: 3, page: 1 } });
            const bills = data.bills || data.data || [];
            setRecentBills(bills.slice(0, 3));
        } catch (err) {
            console.error('Bills fetch error:', err);
        }
    }, []);

    // Fetch chỉ checkin - dùng cho realtime update
    const fetchCheckins = useCallback(async () => {
        try {
            const { data } = await api.get('/customers/checkinAll', { params: { limit: 1000 } });
            const checkins = (data.data || data || []).map((c) => ({
                id: c.customer?.id || c.id,
                name: c.customer?.name || c.name || 'Khách',
                phone: c.customer?.phone || c.phone || '',
                type: c.customer?.type || 'normal',
                point: c.customer?.point || 0,
                checkinTime: c.checkinTime,
            }));
            setCheckinList(checkins);
        } catch (err) {
            console.error('Checkin fetch error:', err);
        }
    }, []);

    // Fetch tất cả lần đầu - có loading spinner
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([fetchStats(), fetchBills(), fetchCheckins()]);
        } finally {
            setLoading(false);
        }
    }, [fetchStats, fetchBills, fetchCheckins]);

    const fetchChart = useCallback(async (period) => {
        setChartLoading(true);
        try {
            const { data } = await api.get('/statistics/profit-chart', {
                params: { period: period || chartPeriod, viewMode: 'day' },
            });

            if (data.chartData && Array.isArray(data.chartData)) {
                const mapped = data.chartData.map((item) => {
                    // API trả label đã đúng format "ngày/tháng" (VD: "25/3")
                    // Chỉ cần dùng trực tiếp, không đảo
                    const name = item.label || item.date || '';
                    const revenue = item.revenue || item.totalRevenue || 0;
                    return { name, revenue };
                });
                setChartData(mapped);
            }
        } catch {
            setChartData([]);
        } finally {
            setChartLoading(false);
        }
    }, []);

    const handlePeriodChange = (value) => {
        setChartPeriod(value);
        setPeriodDropdownOpen(false);
        fetchChart(value);
    };

    useEffect(() => {
        fetchAll();
        fetchChart();
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user?.shopId) {
            getShop(user.shopId)
                .then(({ data }) => {
                    if (data?.timeEnd) setShopTimeEnd(new Date(data.timeEnd));
                })
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Socket realtime: cập nhật từng phần, không loading toàn trang
    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;

        // Statistics thay đổi → chỉ cập nhật KPI + chart + bills
        const onStatsUpdated = () => {
            fetchStats();
            fetchBills();
            fetchChart();
        };

        // Checkin mới → thêm vào list ngay lập tức, không fetch lại
        const onCheckinCreated = (checkin) => {
            const customer = checkin.customer || checkin.Customer || {};
            if (!customer?.id) return;
            setCheckinList((prev) => {
                if (prev.some((c) => c.id === customer.id)) return prev;
                return [
                    {
                        id: customer.id,
                        name: customer.name || 'Khách',
                        phone: customer.phone || '',
                        type: customer.type || 'normal',
                        point: customer.point || 0,
                        checkinTime: checkin.checkinTime || new Date().toISOString(),
                    },
                    ...prev,
                ];
            });
        };

        // Xoá checkin → remove khỏi list ngay lập tức
        const onCheckinDeleted = (customerId) => {
            setCheckinList((prev) => prev.filter((c) => c.id !== customerId));
        };

        // Bill thay đổi → cập nhật bảng hoá đơn gần đây
        const onBillUpsert = () => {
            fetchBills();
            fetchStats();
        };

        const onBillRemove = () => {
            fetchBills();
            fetchStats();
        };

        socket.on('statistics.updated', onStatsUpdated);
        socket.on('checkin.created', onCheckinCreated);
        socket.on('checkin.deleted', onCheckinDeleted);
        socket.on('serving.bill.upsert', onBillUpsert);
        socket.on('serving.bill.remove', onBillRemove);

        return () => {
            socket.off('statistics.updated', onStatsUpdated);
            socket.off('checkin.created', onCheckinCreated);
            socket.off('checkin.deleted', onCheckinDeleted);
            socket.off('serving.bill.upsert', onBillUpsert);
            socket.off('serving.bill.remove', onBillRemove);
        };
    }, [fetchStats, fetchBills, fetchChart]);

    const revenue = stats?.totalRevenue || 0;
    const profit = stats?.totalProfit || 0;
    const todayCustomers = stats?.todayCustomers || 0;
    const servingBills = stats?.servingBills || 0;

    if (loading) {
        return (
            <div className={styles.dashboard} style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
                <div
                    style={{
                        width: 32,
                        height: 32,
                        border: '3px solid #E5E7EB',
                        borderTopColor: '#0098F6',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                    }}
                />
            </div>
        );
    }

    return (
        <div className={styles.dashboard}>
            {/* ===== HEADER ===== */}
            <div className={styles.dashboardHeader}>
                <div className={styles.dashboardHeaderLeft}>
                    <h1>Tổng quan</h1>
                    <p>Chào buổi sáng! Đây là tình hình kinh doanh hôm nay.</p>
                </div>
                <div className={styles.dashboardHeaderRight}>
                    <div className={styles.dashboardHeaderDateLabel}>HÔM NAY</div>
                    <div className={styles.dashboardHeaderDate}>{formatDateVN()}</div>
                </div>
            </div>

            {/* ===== KPI CARDS ===== */}
            <div className={styles.dashboardKpis}>
                <div
                    className={styles.dashboardKpi}
                    onClick={() => setShowMoney(!showMoney)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className={styles.dashboardKpiLeft}>
                        <div className={`${styles.dashboardKpiIcon} ${styles.dashboardKpiIconBlue}`}>
                            <Wallet />
                        </div>
                        <span className={styles.dashboardKpiLabel}>Doanh thu hôm nay</span>
                        <span className={styles.dashboardKpiValue}>
                            {showMoney ? formatCurrency(revenue) : '******'}
                        </span>
                        <span className={`${styles.dashboardKpiChange} ${styles.dashboardKpiChangeNeutral}`}>
                            {showMoney ? 'Ấn để ẩn' : 'Ấn để hiện'}
                        </span>
                    </div>
                </div>

                <div
                    className={styles.dashboardKpi}
                    onClick={() => setShowMoney(!showMoney)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className={styles.dashboardKpiLeft}>
                        <div className={`${styles.dashboardKpiIcon} ${styles.dashboardKpiIconPurple}`}>
                            <PiggyBank />
                        </div>
                        <span className={styles.dashboardKpiLabel}>Lợi nhuận ước tính</span>
                        <span className={styles.dashboardKpiValue}>
                            {showMoney ? formatCurrency(profit) : '******'}
                        </span>
                        <span className={`${styles.dashboardKpiChange} ${styles.dashboardKpiChangeNeutral}`}>
                            {showMoney ? 'Ấn để ẩn' : 'Ấn để hiện'}
                        </span>
                    </div>
                </div>

                <div
                    className={styles.dashboardKpi}
                    onClick={() => navigate('/admin/today-customers')}
                    style={{ cursor: 'pointer' }}
                >
                    <div className={styles.dashboardKpiLeft}>
                        <div className={`${styles.dashboardKpiIcon} ${styles.dashboardKpiIconGreen}`}>
                            <Users />
                        </div>
                        <span className={styles.dashboardKpiLabel}>Khách hàng hôm nay</span>
                        <span className={styles.dashboardKpiValue}>
                            {todayCustomers}
                            <small>khách</small>
                        </span>
                        <span
                            className={`${styles.dashboardKpiChange} ${styles.dashboardKpiChangeUp}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate('/admin/today-customers?type=new');
                            }}
                        >
                            <TrendingUp /> +{stats?.newCustomers || 0} khách mới
                        </span>
                    </div>
                </div>

                <div
                    className={styles.dashboardKpi}
                    onClick={() => navigate('/admin/serving')}
                    style={{ cursor: 'pointer' }}
                >
                    <div className={styles.dashboardKpiLeft}>
                        <div className={`${styles.dashboardKpiIcon} ${styles.dashboardKpiIconOrange}`}>
                            <FileText />
                        </div>
                        <span className={styles.dashboardKpiLabel}>Đang phục vụ</span>
                        <span className={styles.dashboardKpiValue}>
                            {servingBills}
                            <small>đơn</small>
                        </span>
                        <span className={`${styles.dashboardKpiChange} ${styles.dashboardKpiChangeNeutral}`}>
                            Ấn để xem chi tiết
                        </span>
                    </div>
                </div>
            </div>

            {/* ===== EXPIRY WARNING ===== */}
            {shopTimeEnd && !expiryDismissed && (() => {
                const days = Math.ceil((shopTimeEnd - new Date()) / (1000 * 60 * 60 * 24));
                if (days > 3) return null;
                const fmtDate = (d) =>
                    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                const lockDate = new Date(shopTimeEnd);
                lockDate.setDate(lockDate.getDate() + 7);
                return (
                    <div className={styles.dashboardExpiryBanner}>
                        <span>
                            ⚠️ Tài khoản của bạn sẽ <strong>hết hạn vào ngày {fmtDate(shopTimeEnd)}</strong> và sẽ bị khoá
                            vào ngày <strong>{fmtDate(lockDate)}</strong>. Vui lòng liên hệ quản trị viên qua{' '}
                            <a className={styles.dashboardExpiryLink} href="https://zalo.me/0387556219" target="_blank" rel="noreferrer">Zalo</a>{' '}
                            để gia hạn hoặc liên hệ qua{' '}
                            <a className={styles.dashboardExpiryLink} href="https://web.facebook.com/NAVOSotiemkinhdoanh" target="_blank" rel="noreferrer">Fanpage Facebook</a>{' '}
                            của chúng tôi.
                        </span>
                        <button className={styles.dashboardExpiryClose} onClick={() => { localStorage.setItem('expiry_banner_dismissed', '1'); setExpiryDismissed(true); }}>✕</button>
                    </div>
                );
            })()}

            {/* ===== MIDDLE: Chart + Quick Actions ===== */}
            <div className={styles.dashboardMiddle}>
                {/* Chart */}
                <div className={styles.dashboardChartCard}>
                    <div className={styles.dashboardChartHeader}>
                        <div>
                            <div className={styles.dashboardChartTitle}>Biểu đồ doanh thu</div>
                            <div className={styles.dashboardChartSubtitle}>
                                {CHART_PERIODS.find((p) => p.value === chartPeriod)?.label}
                            </div>
                        </div>
                        <div className={styles.dashboardChartPeriodWrapper} ref={periodRef}>
                            <button
                                className={styles.dashboardChartPeriod}
                                onClick={() => setPeriodDropdownOpen(!periodDropdownOpen)}
                            >
                                {CHART_PERIODS.find((p) => p.value === chartPeriod)?.label}
                                <ChevronDown size={16} />
                            </button>
                            {periodDropdownOpen && (
                                <div className={styles.dashboardChartPeriodDropdown}>
                                    {CHART_PERIODS.map((p) => (
                                        <button
                                            key={p.value}
                                            className={`${styles.dashboardChartPeriodOption}${p.value === chartPeriod ? ` ${styles.dashboardChartPeriodOptionActive}` : ''}`}
                                            onClick={() => handlePeriodChange(p.value)}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.dashboardChartBody}>
                        {chartLoading ? (
                            <div className={styles.dashboardChartLoading}>
                                <div className={styles.dashboardChartSpinner} />
                                <span className={styles.dashboardChartLoadingText}>Đang tải dữ liệu...</span>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} barSize={32} barGap={8}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 13, fill: '#6B7280' }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 12, fill: '#6B7280' }}
                                        tickFormatter={(v) => {
                                            if (v >= 1000000) return `${(v / 1000000).toFixed(0)}tr`;
                                            if (v >= 1000) return `${(v / 1000).toFixed(0)}k`;
                                            return v;
                                        }}
                                    />
                                    <Tooltip
                                        formatter={(value) => [formatCurrency(value), 'Doanh thu']}
                                        contentStyle={{
                                            background: '#fff',
                                            border: '1px solid #E5E7EB',
                                            borderRadius: 8,
                                            fontSize: 13,
                                        }}
                                    />
                                    <Bar dataKey="revenue" fill="#0098F6" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className={styles.dashboardActionsCard}>
                    <div className={styles.dashboardActionsTitle}>THAO TÁC NHANH</div>
                    <div className={styles.dashboardActionsList}>
                        <div className={styles.dashboardActionItem} onClick={() => navigate('/admin/bills/create')}>
                            <div className={styles.dashboardActionIcon}>
                                <Plus />
                            </div>
                            <span className={styles.dashboardActionLabel}>Tạo hoá đơn mới</span>
                            <span className={styles.dashboardActionArrow}>
                                <ChevronRight />
                            </span>
                        </div>
                        <div className={styles.dashboardActionItem} onClick={() => navigate('/admin/customers/create')}>
                            <div className={styles.dashboardActionIcon}>
                                <UserPlus />
                            </div>
                            <span className={styles.dashboardActionLabel}>Thêm khách hàng</span>
                            <span className={styles.dashboardActionArrow}>
                                <ChevronRight />
                            </span>
                        </div>
                        <div className={styles.dashboardActionItem} onClick={() => navigate('/admin/bills/waiting')}>
                            <div className={styles.dashboardActionIcon}>
                                <Clock />
                            </div>
                            <span className={styles.dashboardActionLabel}>Đợi phục vụ</span>
                            <span className={styles.dashboardActionArrow}>
                                <ChevronRight />
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ===== BOTTOM: Recent Bills + Checkin ===== */}
            <div className={styles.dashboardBottom}>
                {/* Recent Bills */}
                <div className={styles.dashboardBillsCard}>
                    <div className={styles.dashboardBillsHeader}>
                        <h3 className={styles.dashboardBillsTitle}>Hoá đơn gần đây</h3>
                        <button className={styles.dashboardBillsLink} onClick={() => navigate('/admin/bills')}>
                            Xem tất cả
                        </button>
                    </div>
                    <table className={styles.dashboardBillsTable}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>KHÁCH HÀNG</th>
                                <th>SỐ TIỀN</th>
                                <th>TRẠNG THÁI</th>
                                <th>THỜI GIAN</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentBills.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ textAlign: 'center', color: '#6B7280', padding: 32 }}>
                                        Chưa có hoá đơn nào
                                    </td>
                                </tr>
                            ) : (
                                recentBills.map((bill) => {
                                    const name = bill.customer?.name || 'Khách lẻ';
                                    const statusClass = STATUS_CLASS[bill.status] || 'paid';
                                    return (
                                        <tr
                                            key={bill.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/admin/bills/${bill.id}`)}
                                        >
                                            <td>
                                                <span className={styles.dashboardBillId}>
                                                    {bill.code || `#${bill.id}`}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.dashboardBillCustomer}>
                                                    <div
                                                        className={styles.dashboardBillAvatar}
                                                        style={{ background: getColor(name) }}
                                                    >
                                                        {getInitials(name)}
                                                    </div>
                                                    {name}
                                                </div>
                                            </td>
                                            <td>{formatCurrency(bill.total)}</td>
                                            <td>
                                                <span
                                                    className={`${styles.dashboardBillStatus} ${STATUS_STYLE_MAP[statusClass] || styles.dashboardBillStatusPaid}`}
                                                >
                                                    {STATUS_LABELS[bill.status] || bill.status}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={styles.dashboardBillTime}>
                                                    {formatTime(bill.checkoutTime)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Checkin Queue */}
                <div className={styles.dashboardCheckinCard}>
                    <div className={styles.dashboardCheckinHeader}>
                        <h3 className={styles.dashboardCheckinTitle}>Đang Check-in</h3>
                        <span className={styles.dashboardCheckinLive}>LIVE</span>
                    </div>

                    <div className={styles.dashboardCheckinList}>
                        {checkinList.length === 0 ? (
                            <div className={styles.dashboardCheckinEmpty}>Chưa có khách check-in</div>
                        ) : (
                            checkinList.map((item, idx) => (
                                <div
                                    key={`${item.id}-${idx}`}
                                    className={styles.dashboardCheckinItem}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() =>
                                        navigate('/admin/bills/create', { state: { preselectedCustomer: item } })
                                    }
                                >
                                    <div
                                        className={styles.dashboardCheckinAvatar}
                                        style={{ background: getColor(item.name) }}
                                    >
                                        {getInitials(item.name)}
                                        <span className={styles.dashboardCheckinDot} />
                                    </div>
                                    <div className={styles.dashboardCheckinInfo}>
                                        <div className={styles.dashboardCheckinName}>{item.name}</div>
                                        <div className={styles.dashboardCheckinService}>SĐT: {item.phone || '-'}</div>
                                    </div>
                                    <div className={styles.dashboardCheckinTime}>{timeAgo(item.checkinTime)}</div>
                                </div>
                            ))
                        )}
                    </div>

                    {checkinList.length > 0 && (
                        <div className={styles.dashboardCheckinFooter}>
                            Quản lý hàng đợi ({checkinList.length} khách đang chờ)
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
